import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Clock, Upload, Image, X } from "lucide-react";
import { format } from "date-fns";
import BackButton from "@/components/BackButton";

// 24 hourly slots (01:00 - 24:00)
// Display shows 01:00-24:00, value stores the actual hour for backend
const HOURS = Array.from({ length: 24 }, (_, i) => {
  const displayHour = i + 1; // 1-24
  const storageHour = displayHour === 24 ? 0 : displayHour; // 24:00 maps to hour 0
  return {
    value: String(storageHour),
    label: `${String(displayHour).padStart(2, "0")}:00`,
    range: `${String(displayHour).padStart(2, "0")}:00 - ${String(displayHour).padStart(2, "0")}:59`,
  };
});

export default function UploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [date, setDate] = useState(new Date());
  const [hour, setHour] = useState("1"); // Default to 01:00
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith("image/")) {
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const clearImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!uploadedImage) return;

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", uploadedImage);
      formData.append("year", String(date.getFullYear()));
      formData.append("month", String(date.getMonth() + 1));
      formData.append("day", String(date.getDate()));
      formData.append("hour", hour);

      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/qimen/upload-chart-image`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        // Navigate to result page with the uploaded image URL
        // Construct full URL for the image
        const fullImageUrl = data.image_url.startsWith("http") 
          ? data.image_url 
          : `${API}${data.image_url}`;
        
        const params = new URLSearchParams({
          year: String(date.getFullYear()),
          month: String(date.getMonth() + 1),
          day: String(date.getDate()),
          hour,
          minute: "0",
          system: "selectZhiRun",
        });
        navigate(`/result?${params.toString()}`);
      } else {
        alert(data.error || "Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      alert("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="upload-page" data-testid="upload-page">
      <div className="upload-bg-pattern" />
      <div className="upload-container">
        {/* Back Button */}
        <BackButton />

        {/* Header */}
        <div className="upload-header">
          <div className="upload-logo-mark">
            <Upload className="upload-logo-icon" />
          </div>
          <h1 className="upload-title" data-testid="upload-title">
            Upload Chart Image
          </h1>
          <p className="upload-subtitle">
            Upload a Qi Men chart image to transcribe and store
          </p>
        </div>

        {/* Form Card */}
        <div className="upload-form-card" data-testid="upload-form">
          {/* Date Picker */}
          <div className="form-group">
            <label className="form-label">
              <CalendarIcon size={14} />
              Select Date
            </label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="date-picker-btn"
                  data-testid="upload-date-picker"
                >
                  <CalendarIcon size={16} />
                  {format(date, "MMMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="calendar-popover" align="center">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => {
                    if (d) {
                      setDate(d);
                      setCalendarOpen(false);
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Hour Select */}
          <div className="form-group">
            <label className="form-label">
              <Clock size={14} />
              Select Hour
            </label>
            <Select value={hour} onValueChange={setHour}>
              <SelectTrigger className="hour-select-full" data-testid="upload-hour-select">
                <SelectValue placeholder="Hour">
                  {HOURS.find(h => h.value === hour)?.range || "Select hour"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {HOURS.map((h) => (
                  <SelectItem key={h.value} value={h.value}>
                    {h.range}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Image Upload Area */}
          <div className="form-group">
            <label className="form-label">
              <Image size={14} />
              Chart Image
            </label>
            
            {!imagePreview ? (
              <div
                className={`upload-dropzone ${dragActive ? "drag-active" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-dropzone"
              >
                <Upload size={32} className="dropzone-icon" />
                <p className="dropzone-text">
                  Drag & drop an image here, or click to select
                </p>
                <p className="dropzone-hint">
                  Supports PNG, JPG, JPEG
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleInputChange}
                  className="hidden-input"
                  data-testid="upload-file-input"
                />
              </div>
            ) : (
              <div className="upload-preview" data-testid="upload-preview">
                <img src={imagePreview} alt="Chart preview" className="preview-image" />
                <Button
                  variant="ghost"
                  className="clear-image-btn"
                  onClick={clearImage}
                  data-testid="clear-image-btn"
                >
                  <X size={18} />
                </Button>
              </div>
            )}
          </div>

          {/* Upload Button */}
          <Button
            className="upload-submit-btn"
            onClick={handleUpload}
            disabled={!uploadedImage || uploading}
            data-testid="upload-submit-btn"
          >
            {uploading ? "Uploading..." : "Upload & View Chart"}
            <Upload size={18} />
          </Button>
        </div>

        {/* Footer */}
        <div className="upload-footer">
          <p>Qi Men Dun Jia Plotter</p>
          <p className="footer-copyright">Copyright © 2026 Steven Yong - Digital Dive Coaching Sdn. Bhd. All rights reserved worldwide.</p>
        </div>
      </div>
    </div>
  );
}
